import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Loader2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getMessageEvent,
  getMessageEventStats,
  listMessageEventExecutions,
  type MessageEventExecution,
  type MessageEventStatus,
} from './api';

const PAGE_SIZE = 50;

const STATUS_OPTIONS: Array<{ value: MessageEventStatus | ''; label: string }> = [
  { value: '', label: 'Todos status' },
  { value: 'sent', label: 'Enviado' },
  { value: 'pending', label: 'Pendente' },
  { value: 'processing', label: 'Processando' },
  { value: 'failed', label: 'Falhou' },
  { value: 'invalid_payload', label: 'Payload inválido' },
  { value: 'channel_offline', label: 'Canal offline' },
  { value: 'auth_failed', label: 'Auth falhou' },
];

const STATUS_LABEL: Record<MessageEventStatus, { label: string; color: string }> = {
  pending: { label: 'Pendente', color: 'text-n-600 bg-n-100' },
  processing: { label: 'Processando', color: 'text-brand-700 bg-brand-50' },
  sent: { label: 'Enviado', color: 'text-success-700 bg-success-50' },
  failed: { label: 'Falhou', color: 'text-danger-700 bg-danger-50' },
  invalid_payload: { label: 'Payload inválido', color: 'text-warning-700 bg-warning-50' },
  channel_offline: { label: 'Canal offline', color: 'text-warning-700 bg-warning-50' },
  auth_failed: { label: 'Auth falhou', color: 'text-danger-700 bg-danger-50' },
};

export function MessageEventExecutionsPage({
  eventId: eventIdProp,
  onClose,
}: {
  eventId?: number;
  /** Quando passado, vira modal overlay com botão fechar. */
  onClose?: () => void;
} = {}) {
  // biome-ignore lint/suspicious/noExplicitAny: dynamic
  const params = useParams({ strict: false }) as any;
  const id = eventIdProp ?? Number(params?.id);

  const [statusFilter, setStatusFilter] = useState<MessageEventStatus | ''>('');
  const [selected, setSelected] = useState<MessageEventExecution | null>(null);

  const eventQ = useQuery({
    queryKey: ['message-event', id],
    queryFn: () => getMessageEvent(id),
    enabled: Number.isFinite(id),
  });
  const statsQ = useQuery({
    queryKey: ['message-event-stats', id],
    queryFn: () => getMessageEventStats(id),
    enabled: Number.isFinite(id),
  });
  const execQ = useInfiniteQuery({
    queryKey: ['message-event-executions', id, statusFilter],
    queryFn: ({ pageParam }) =>
      listMessageEventExecutions(id, {
        ...(statusFilter && { status: statusFilter }),
        limit: PAGE_SIZE,
        ...(typeof pageParam === 'number' && { cursor: pageParam }),
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: Number.isFinite(id),
  });

  const executions = useMemo<MessageEventExecution[]>(
    () => execQ.data?.pages.flatMap((p) => p.items) ?? [],
    [execQ.data],
  );

  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !execQ.hasNextPage || execQ.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) void execQ.fetchNextPage();
        }
      },
      { rootMargin: '200px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [execQ.hasNextPage, execQ.isFetchingNextPage, execQ.fetchNextPage]);

  // Mede primeira linha + header → maxHeight = 10 linhas exatas.
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [scrollMaxHeight, setScrollMaxHeight] = useState<number | null>(null);
  useEffect(() => {
    const t = tableRef.current;
    if (!t || executions.length === 0) return;
    const headerRow = t.querySelector('thead tr') as HTMLElement | null;
    const firstBodyRow = t.querySelector('tbody tr') as HTMLElement | null;
    if (!headerRow || !firstBodyRow) return;
    setScrollMaxHeight(headerRow.offsetHeight + firstBodyRow.offsetHeight * 10);
  }, [executions.length]);

  // ESC fecha o modal (quando renderizado como overlay).
  useEffect(() => {
    if (!onClose) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const stats = statsQ.data;

  return (
    <div className="flex flex-1 flex-col gap-4">
      <header className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="font-bold text-[20px] text-n-900" style={{ letterSpacing: '-.02em' }}>
            Histórico de execuções — {eventQ.data?.name ?? '…'}
          </h1>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded text-n-500 hover:bg-n-100 hover:text-n-700"
            title="Fechar"
          >
            <X size={18} />
          </button>
        )}
      </header>

      {/* Indicators */}
      {stats && (
        <div className="grid grid-cols-3 gap-3">
          {(['24h', '7d', '30d'] as const).map((bucket) => {
            const s = stats[bucket];
            const rate = s && s.total > 0 ? Math.round((s.sent / s.total) * 100) : 0;
            return (
              <div key={bucket} className="rounded-md border border-n-200 bg-n-0 p-4 shadow-sm">
                <div className="text-[11px] font-bold uppercase tracking-[.04em] text-n-500">
                  Últimas {bucket}
                </div>
                <div className="mt-2 flex items-baseline gap-3">
                  <span className="font-bold text-[24px] text-n-900">{s?.total ?? 0}</span>
                  <span className="text-[11px] text-n-500">execuções</span>
                </div>
                <div className="mt-1 text-[12px]">
                  <span className="text-success-700">✓ {s?.sent ?? 0}</span>
                  {' · '}
                  <span className="text-danger-700">✗ {s?.failed ?? 0}</span>
                  {' · '}
                  <span className="text-n-600">{rate}% sucesso</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filtros */}
      <div className="flex items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as MessageEventStatus | '')}
          className="h-9 rounded border border-n-200 bg-n-0 px-2 text-[12px]"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <span className="text-[11px] text-n-500">
          {executions.length} resultado(s)
          {execQ.hasNextPage && ' (carregando mais ao rolar)'}
        </span>
      </div>

      {/* Tabela */}
      <div
        className="overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm"
        style={scrollMaxHeight ? { maxHeight: scrollMaxHeight } : undefined}
      >
        <table ref={tableRef} className="w-full text-[13px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Quando
              </th>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Status
              </th>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Phone
              </th>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Ticket
              </th>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Ação
              </th>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Erro
              </th>
            </tr>
          </thead>
          <tbody>
            {execQ.isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center">
                  <Loader2 className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!execQ.isLoading && executions.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-12 text-center text-n-500">
                  Nenhuma execução ainda.
                </td>
              </tr>
            )}
            {executions.map((e) => {
              const statusInfo = STATUS_LABEL[e.status];
              const isSelected = selected?.id === e.id;
              return (
                <tr
                  key={e.id}
                  onClick={() => setSelected(isSelected ? null : e)}
                  className={`cursor-pointer border-b border-n-100 last:border-b-0 ${
                    isSelected ? 'bg-brand-50' : 'hover:bg-n-50'
                  }`}
                >
                  <td className="px-[14px] py-[11px] text-n-600">
                    {new Date(e.received_at).toLocaleString('pt-BR')}
                  </td>
                  <td className="px-[14px] py-[11px]">
                    <span
                      className={`rounded-pill px-2 py-[2px] text-[11px] font-semibold ${statusInfo.color}`}
                    >
                      {statusInfo.label}
                    </span>
                  </td>
                  <td className="px-[14px] py-[11px] text-[11px] text-n-700">
                    {e.resolved_phone ?? '—'}
                  </td>
                  <td className="px-[14px] py-[11px] text-n-700">
                    {e.ticket_id ? `#${e.ticket_id}` : '—'}
                  </td>
                  <td className="px-[14px] py-[11px] text-n-700">{e.action_applied ?? '—'}</td>
                  <td className="px-[14px] py-[11px] text-danger-600">
                    {e.error_code ? `${e.error_code}` : '—'}
                  </td>
                </tr>
              );
            })}
            {execQ.hasNextPage && (
              <tr ref={sentinelRef}>
                <td colSpan={6} className="py-4 text-center text-[12px] text-n-500">
                  {execQ.isFetchingNextPage ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> Carregando mais…
                    </span>
                  ) : (
                    'Role para carregar mais'
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Detail panel */}
      {selected && (
        <div className="rounded-md border border-n-200 bg-n-0 p-4 shadow-sm">
          <h3 className="font-bold text-[14px] text-n-900">Detalhe — execução #{selected.id}</h3>
          <div className="mt-3 grid grid-cols-2 gap-3 text-[12px]">
            <div>
              <span className="text-n-500">Recebido em:</span>{' '}
              <span className="text-n-700">
                {new Date(selected.received_at).toLocaleString('pt-BR')}
              </span>
            </div>
            <div>
              <span className="text-n-500">Concluído em:</span>{' '}
              <span className="text-n-700">
                {selected.completed_at
                  ? new Date(selected.completed_at).toLocaleString('pt-BR')
                  : '—'}
              </span>
            </div>
            <div>
              <span className="text-n-500">Contato:</span>{' '}
              <span className="text-n-700">{selected.contact_id ?? '—'}</span>
            </div>
            <div>
              <span className="text-n-500">Mensagem:</span>{' '}
              <span className="text-n-700">{selected.message_id ?? '—'}</span>
            </div>
          </div>
          {selected.error_detail && (
            <div className="mt-3 rounded border border-danger-200 bg-danger-50 p-2 text-[11px] text-danger-700">
              <strong>{selected.error_code}:</strong> {selected.error_detail}
            </div>
          )}
          <div className="mt-3">
            <div className="mb-1 text-[11px] font-semibold text-n-600">Payload recebido</div>
            <pre className="max-h-64 overflow-auto rounded border border-n-200 bg-n-50 p-2 font-mono text-[11px] text-n-700">
              {JSON.stringify(selected.raw_payload, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
